import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Trash2
} from 'lucide-react';
import api from '../../utils/api';

const sectionOptions = [
  { value: 'feature', label: '新增功能' },
  { value: 'fix', label: '问题修复' },
  { value: 'optimize', label: '优化调整' },
  { value: 'notice', label: '注意事项' }
];

const emptyNote = () => ({
  localId: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  section_key: 'feature',
  section_title: '新增功能',
  content: '',
  sort_order: 1
});

const emptyForm = () => ({
  id: null,
  platform: 'windows',
  version: '',
  build_number: '',
  title: '',
  summary: '',
  download_url: '',
  force_update: false,
  status: 'draft',
  published_at: '',
  notes: [emptyNote()]
});

function toDateTimeLocal(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.slice(0, 16).replace(' ', 'T');
}

function normalizeNotesForForm(notes) {
  if (!Array.isArray(notes) || !notes.length) return [emptyNote()];
  return notes.map((item, index) => ({
    localId: `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`,
    section_key: item.section_key || 'feature',
    section_title: item.section_title || '更新内容',
    content: item.content || '',
    sort_order: item.sort_order ?? index + 1
  }));
}

function getSectionTitle(sectionKey) {
  return sectionOptions.find((item) => item.value === sectionKey)?.label || '更新内容';
}

function buildPayload(form) {
  return {
    platform: form.platform,
    version: form.version,
    build_number: form.build_number === '' ? null : Number(form.build_number),
    title: form.title,
    summary: form.summary,
    download_url: form.download_url,
    force_update: !!form.force_update,
    status: form.status,
    published_at: form.published_at || null,
    notes: (form.notes || [])
      .map((item, index) => ({
        section_key: item.section_key,
        section_title: item.section_title,
        content: item.content,
        sort_order: Number(item.sort_order || index + 1)
      }))
      .filter((item) => String(item.content || '').trim())
  };
}

export default function ClientReleasesPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(emptyForm());

  const isEditing = !!form.id;

  const platformOptions = useMemo(() => ([
    { value: 'all', label: '通用' },
    { value: 'windows', label: 'Windows' },
    { value: 'macos-intel', label: 'macOS Intel' },
    { value: 'macos-apple', label: 'macOS Apple' }
  ]), []);

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: '草稿' },
    { value: 'published', label: '已发布' },
    { value: 'offline', label: '已下线' }
  ]), []);

  useEffect(() => {
    fetchReleases();
  }, [platformFilter, statusFilter]);

  const fetchReleases = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/client-releases', {
        params: {
          page: 1,
          limit: 100,
          include_notes: 1,
          platform: platformFilter || undefined,
          status: statusFilter || undefined
        }
      });
      if (response.data.success) {
        setReleases(response.data.list || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || '获取客户端版本列表失败');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
  };

  const startEdit = (item) => {
    setForm({
      id: item.id,
      platform: item.platform || 'windows',
      version: item.version || '',
      build_number: item.build_number == null ? '' : String(item.build_number),
      title: item.title || '',
      summary: item.summary || '',
      download_url: item.download_url || '',
      force_update: !!item.force_update,
      status: item.status || 'draft',
      published_at: toDateTimeLocal(item.published_at),
      notes: normalizeNotesForForm(item.note_items)
    });
    setError('');
    setSuccessMsg('');
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNoteChange = (localId, key, value) => {
    setForm((prev) => ({
      ...prev,
      notes: prev.notes.map((item) => (
        item.localId === localId
          ? {
              ...item,
              [key]: value,
              ...(key === 'section_key' ? { section_title: getSectionTitle(value) } : null)
            }
          : item
      ))
    }));
  };

  const addNote = () => {
    setForm((prev) => ({
      ...prev,
      notes: [
        ...prev.notes,
        { ...emptyNote(), sort_order: prev.notes.length + 1 }
      ]
    }));
  };

  const removeNote = (localId) => {
    setForm((prev) => {
      const nextNotes = prev.notes.filter((item) => item.localId !== localId);
      return {
        ...prev,
        notes: nextNotes.length ? nextNotes : [emptyNote()]
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const payload = buildPayload(form);
      const response = isEditing
        ? await api.put(`/admin/client-releases/${form.id}`, payload)
        : await api.post('/admin/client-releases', payload);

      if (response.data.success) {
        setSuccessMsg(isEditing ? '客户端版本更新成功' : '客户端版本创建成功');
        resetForm();
        fetchReleases();
      }
    } catch (err) {
      setError(err.response?.data?.error || '保存客户端版本失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`确认删除版本 ${item.version} 吗？`)) return;
    setDeletingId(item.id);
    setError('');
    setSuccessMsg('');
    try {
      const response = await api.delete(`/admin/client-releases/${item.id}`);
      if (response.data.success) {
        setSuccessMsg('客户端版本删除成功');
        if (form.id === item.id) {
          resetForm();
        }
        fetchReleases();
      }
    } catch (err) {
      setError(err.response?.data?.error || '删除客户端版本失败');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-8">
      {error ? (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      ) : null}

      {successMsg ? (
        <div className="bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-4 rounded-xl flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <p>{successMsg}</p>
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">客户端版本管理</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">支持维护最新一条、最新 N 条和全部历史版本的更新内容。</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white"
            >
              <option value="">全部平台</option>
              {platformOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white"
            >
              <option value="">全部状态</option>
              {statusOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
            <button
              onClick={fetchReleases}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200"
            >
              <RefreshCw className="w-4 h-4" />
              刷新
            </button>
            <button
              onClick={resetForm}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white"
            >
              <Plus className="w-4 h-4" />
              新建版本
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-0">
          <div className="xl:col-span-2 border-r border-gray-200 dark:border-gray-700">
            <div className="p-6 space-y-4 max-h-[900px] overflow-y-auto">
              {loading ? (
                <div className="py-12 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : releases.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                  暂无客户端版本数据
                </div>
              ) : releases.map((item) => (
                <div key={item.id} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.title}</span>
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs">{item.platform}</span>
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs">{item.status}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">版本 {item.version}{item.build_number != null ? ` / build ${item.build_number}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(item)}
                        className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        disabled={deletingId === item.id}
                        onClick={() => handleDelete(item)}
                        className={`p-2 rounded-lg text-white ${deletingId === item.id ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
                      >
                        {deletingId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {item.summary ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300">{item.summary}</p>
                  ) : null}
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <p>发布时间：{item.published_at || '-'}</p>
                    <p>强制更新：{item.force_update ? '是' : '否'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="xl:col-span-3 p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{isEditing ? '编辑客户端版本' : '新建客户端版本'}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">客户端读取接口为 `/api/client/releases`，无需 token。</p>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm font-medium"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存版本
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">平台</label>
                <select name="platform" value={form.platform} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white">
                  {platformOptions.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">状态</label>
                <select name="status" value={form.status} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white">
                  {statusOptions.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">版本号</label>
                <input name="version" value={form.version} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" placeholder="例如 2.3.1" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Build Number</label>
                <input name="build_number" type="number" value={form.build_number} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" placeholder="可选" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">标题</label>
                <input name="title" value={form.title} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" placeholder="例如 OpenClaw 2.3.1" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">摘要</label>
                <textarea name="summary" value={form.summary} onChange={handleFormChange} rows={3} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" placeholder="版本摘要，可选" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">下载地址</label>
                <input name="download_url" value={form.download_url} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">发布时间</label>
                <input name="published_at" type="datetime-local" value={form.published_at} onChange={handleFormChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" />
              </div>
              <div className="flex items-center gap-3 pt-8">
                <input id="force_update" name="force_update" type="checkbox" checked={form.force_update} onChange={handleFormChange} className="w-4 h-4" />
                <label htmlFor="force_update" className="text-sm font-medium text-gray-700 dark:text-gray-300">强制更新</label>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">更新内容</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">支持多个区域，每个区域可对应一条内容。</p>
                </div>
                <button
                  type="button"
                  onClick={addNote}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                >
                  <Plus className="w-4 h-4" />
                  新增内容
                </button>
              </div>

              <div className="space-y-4">
                {form.notes.map((item, index) => (
                  <div key={item.localId} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">更新区块</label>
                        <select
                          value={item.section_key}
                          onChange={(e) => handleNoteChange(item.localId, 'section_key', e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white"
                        >
                          {sectionOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">展示标题</label>
                        <input
                          value={item.section_title}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-300 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                        <input type="number" value={item.sort_order} onChange={(e) => handleNoteChange(item.localId, 'sort_order', e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容</label>
                      <textarea value={item.content} onChange={(e) => handleNoteChange(item.localId, 'content', e.target.value)} rows={3} className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg dark:text-white" placeholder="请输入这一条更新内容" />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeNote(item.localId)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        删除内容
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
